/**
 * Legacy route. The separate "manual licence" form was folded into the
 * photo-first screen, so this only forwards.
 *
 * It forwards after a paint, though, and it used to paint nothing at all — no
 * header, no back button, an empty page the width of the screen. That is
 * indistinguishable from the blank-screen failure mode the app has a
 * `ConfigErrorScreen` safety net for, and it is what a deep link or an old
 * bookmark into this URL lands on. It now paints the destination's own header
 * and the same centred spinner the licence screen shows while it loads, so the
 * hand-off reads as one screen settling rather than two screens flashing.
 */
import { useEffect } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'

export default function LicenseManualRedirect() {
  const router = useRouter()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  useEffect(() => {
    router.replace('/(tabs)/profile/verification/license' as never)
  }, [router])

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('verification.license_title')} />
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          // The licence screen's own body starts under the header, so pulling
          // the spinner up by the header's height keeps it on the optical
          // centre of the page instead of the centre of what is left.
          paddingBottom: SPACING['2xl'],
        }}
      >
        {/* `textBrand`, not the raw brand purple: #480C82 on the dark page is
            ~1.5:1, and an invisible spinner on an otherwise empty screen is
            the blank page again. */}
        <ActivityIndicator color={colors.textBrand} accessibilityLabel={t('common.loading')} />
      </View>
    </ScreenContainer>
  )
}
